import { Grid, TransformControls } from "@react-three/drei";
import { useFrame } from "@react-three/fiber";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import { useSceneStore } from "../store/sceneStore";
import { commitUndo, pauseUndo } from "../lib/undo";
import { SceneObjectMesh } from "./SceneObjectMesh";
import { MeasureOverlay } from "./MeasureOverlay";

interface Props {
  objectsGroupRef: React.RefObject<THREE.Group>;
  orbitControlsRef: React.RefObject<OrbitControlsImpl>;
}

export function SceneContent({ objectsGroupRef, orbitControlsRef }: Props) {
  const objects = useSceneStore((s) => s.objects);
  const selectedId = useSceneStore((s) => s.selectedId);
  const interactionMode = useSceneStore((s) => s.interactionMode);
  const transformMode = useSceneStore((s) => s.transformMode);
  const settings = useSceneStore((s) => s.settings);
  const updateObject = useSceneStore((s) => s.updateObject);

  const selectedGroupRef = useRef<THREE.Group>(null);

  // If the gizmo unmounts mid-drag (e.g. selection changes before the pointer is
  // released), its onMouseUp never fires and orbit controls would stay disabled.
  // (The undo history's own pause/resume is covered globally by useUndoSafetyNet.)
  useEffect(() => {
    return () => {
      if (orbitControlsRef.current) orbitControlsRef.current.enabled = true;
    };
  }, [selectedId, interactionMode, orbitControlsRef]);

  useFrame((_, delta) => {
    if (settings.autoRotate && objectsGroupRef.current) {
      objectsGroupRef.current.rotation.y += delta * 0.4;
    }
  });

  const handleMouseDown = () => {
    if (orbitControlsRef.current) orbitControlsRef.current.enabled = false;
    pauseUndo();
  };

  const handleMouseUp = () => {
    if (orbitControlsRef.current) orbitControlsRef.current.enabled = true;
    commitUndo();
  };

  const handleObjectChange = () => {
    const target = selectedGroupRef.current;
    if (!target || !selectedId) return;
    updateObject(selectedId, {
      position: target.position.toArray() as [number, number, number],
      rotation: [target.rotation.x, target.rotation.y, target.rotation.z],
      scale: target.scale.toArray() as [number, number, number],
    });
  };

  return (
    <>
      <ambientLight intensity={0.6} />
      <directionalLight position={[6, 10, 6]} intensity={1.4} castShadow shadow-mapSize={[2048, 2048]} />
      <hemisphereLight intensity={0.3} groundColor="#111318" />

      <mesh rotation-x={-Math.PI / 2} position-y={-0.01} receiveShadow visible={settings.groundVisible}>
        <planeGeometry args={[2000, 2000]} />
        <meshStandardMaterial color="#1f232b" />
      </mesh>
      {settings.gridVisible && (
        <Grid
          infiniteGrid
          cellSize={10}
          sectionSize={50}
          cellColor="#333843"
          sectionColor="#454c5a"
          fadeDistance={2000}
        />
      )}

      <group ref={objectsGroupRef}>
        {objects.map((obj) => (
          <group
            key={obj.id}
            ref={obj.id === selectedId ? selectedGroupRef : undefined}
            position={obj.position}
            rotation={obj.rotation}
            scale={obj.scale}
          >
            <SceneObjectMesh data={obj} />
          </group>
        ))}
      </group>

      {selectedId && interactionMode === "select" && (
        <TransformControls
          key={selectedId}
          object={selectedGroupRef as React.MutableRefObject<THREE.Object3D>}
          mode={transformMode}
          size={1.6}
          onMouseDown={handleMouseDown}
          onMouseUp={handleMouseUp}
          onObjectChange={handleObjectChange}
        />
      )}

      <MeasureOverlay />
    </>
  );
}
